import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { AdminStats, ActivityLogEntry } from '@/types'

interface AdminClient {
  id: string
  name: string
  username: string
  allowedPages?: string[] | null
  accountCount: number
  igActive: number
  fbActive: number
}

export function useAdminStats() {
  return useQuery<AdminStats>({
    queryKey: ['admin-stats'],
    queryFn: () => api.get('/admin/stats'),
  })
}

export function useAdminClients() {
  return useQuery<AdminClient[]>({
    queryKey: ['admin-clients'],
    queryFn: () => api.get('/admin/clients'),
  })
}

export function useCreateClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; username: string; password: string; allowedPages?: string[] }) =>
      api.post('/admin/clients', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-clients', 'admin-stats'] }),
  })
}

export function useDeleteClient() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (clientId: string) => api.delete(`/admin/clients/${clientId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-clients', 'admin-stats'] }),
  })
}

export function useActivityLog() {
  return useQuery<ActivityLogEntry[]>({
    queryKey: ['activity-log'],
    queryFn: () => api.get('/monitor/activity'),
    refetchInterval: 3000,
  })
}

interface ClientMediaFolder {
  id: string
  name: string
  type: string
  parentId: string | null
  imageCount: number
}

interface ClientMediaFoldersResponse {
  client: { id: string; name: string; username: string }
  folders: ClientMediaFolder[]
}

export function useClientMediaFolders(clientId: string | null) {
  return useQuery<ClientMediaFoldersResponse>({
    queryKey: ['admin-client-media', clientId],
    queryFn: () => api.get(`/admin/clients/${clientId}/media-folders`),
    enabled: !!clientId,
  })
}

export function useAdminUsers() {
  return useQuery<{ id: string; name: string; username: string }[]>({
    queryKey: ['admin-all-users'],
    queryFn: () => api.get('/admin/all-users'),
  })
}
